import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiDownload, FiFileText, FiGrid } from 'react-icons/fi';

const medicalTheme = {
  surface: '#f8fbfd',
  surfaceHover: '#e4f4f7',
  border: '#7aa7b6',
  borderStrong: '#4d7f91',
  text: '#134b5f',
  textSoft: '#335e6f',
  shadow: 'rgba(19, 75, 95, 0.12)',
  shadowHover: 'rgba(19, 75, 95, 0.16)',
  accentSoft: '#e6f5f7',
};

const exportButtonStyle = {
  background: `linear-gradient(135deg, ${medicalTheme.surface} 0%, #ffffff 100%)`,
  color: medicalTheme.text,
  border: `1.5px solid ${medicalTheme.border}`,
  borderRadius: '14px',
  padding: '12px 18px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  boxShadow: `0 10px 24px ${medicalTheme.shadow}, inset 0 1px 0 rgba(255, 255, 255, 0.8)`,
  transition: 'all 0.25s ease',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  position: 'relative',
  outline: 'none',
  letterSpacing: '0.01em',
};

const exportButtonHoverStyle = {
  ...exportButtonStyle,
  transform: 'translateY(-2px)',
  background: `linear-gradient(135deg, ${medicalTheme.surfaceHover} 0%, #ffffff 100%)`,
  borderColor: medicalTheme.borderStrong,
  boxShadow: `0 14px 30px ${medicalTheme.shadowHover}, inset 0 1px 0 rgba(255, 255, 255, 0.95)`,
};

const dropdownStyle = {
  position: 'absolute',
  top: '100%',
  right: 0,
  background: 'linear-gradient(180deg, #ffffff 0%, #f6fbfc 100%)',
  borderRadius: '16px',
  boxShadow: '0 18px 42px rgba(19, 75, 95, 0.14)',
  border: '1px solid rgba(122, 167, 182, 0.35)',
  padding: '8px 0',
  minWidth: '180px',
  zIndex: 1000,
  animation: 'slideDown 0.2s ease-out',
};

const dropdownItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  cursor: 'pointer',
  transition: 'background 0.2s ease',
  fontSize: 14,
  color: medicalTheme.textSoft,
  border: 'none',
  background: 'none',
  width: '100%',
  textAlign: 'left',
};

const ExportButton = ({ 
  onExport, 
  formats = ['PDF', 'CSV'], 
  disabled = false,
  customLabel = 'Export',
  size = 'medium' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && buttonRef.current.contains(event.target)
      ) return;
      if (
        dropdownRef.current && dropdownRef.current.contains(event.target)
      ) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const desiredWidth = Math.max(160, Math.min(320, rect.width));
      let left = rect.left + window.scrollX;
      const rightEdge = left + desiredWidth;
      const padding = 12;
      if (rightEdge > window.innerWidth - padding) {
        left = Math.max(padding, window.innerWidth - desiredWidth - padding) + window.scrollX;
      }

      setCoords({
        top: rect.bottom + window.scrollY,
        left,
        width: desiredWidth
      });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen]);

  const handleExport = (format) => {
    setIsOpen(false);
    onExport(format);
  };

  const getIconForFormat = (format) => {
    switch (format.toLowerCase()) {
      case 'pdf':
        return <FiFileText size={16} />;
      case 'csv':
        return <FiGrid size={16} />;
      default:
        return <FiDownload size={16} />;
    }
  };

  const buttonSizeStyle = size === 'small' ? { padding: '8px 16px', fontSize: 12 } : {};

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        style={{
          ...(isHovered ? exportButtonHoverStyle : exportButtonStyle),
          ...buttonSizeStyle,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <FiDownload size={16} />
        {customLabel}
      </button>

      {isOpen && coords && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            ...dropdownStyle,
            position: 'absolute',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            width: `${coords.width}px`,
            maxHeight: '60vh',
            overflowY: 'auto',
            zIndex: 20000
          }}
        >
          {formats.map((format) => (
            <button
              key={format}
              style={dropdownItemStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = medicalTheme.accentSoft;
                e.currentTarget.style.color = medicalTheme.text;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = medicalTheme.textSoft;
              }}
              onClick={() => handleExport(format)}
            >
              {getIconForFormat(format)}
              Export as {format}
            </button>
          ))}
        </div>,
        document.body
      )}

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ExportButton; 